import React, { useRef, useState } from 'react';
import henceforthApi from '../../../utils/henceforthApi';
import { UploadCloud, X, ArrowLeft, ArrowRight, Star, AlertCircle } from 'lucide-react';

export interface ImageItem {
  id: string;
  type: 'existing' | 'new';
  url: string;
  file?: File;
  fileName?: string;
}

interface ImageUploaderProps {
  existingImages: string[];
  newFiles: File[];
  onChange: (data: { existingImages: string[]; newFiles: File[] }) => void;
  error?: string;
  maxFiles?: number;
  maxSizeMB?: number;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  existingImages = [],
  newFiles = [],
  onChange,
  error,
  maxFiles = 10,
  maxSizeMB = 5,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const imageList = React.useMemo<ImageItem[]>(() => {
    const list: ImageItem[] = [];

    existingImages.forEach((fileName, index) => {
      let url = fileName;
      if (!fileName.startsWith('http') && !fileName.startsWith('blob:')) {
        const root = henceforthApi.API_FILE_ROOT_MEDIUM || henceforthApi.API_FILE_ROOT_ORIGINAL || henceforthApi.API_ROOT_IMAGEUPLOAD || '';
        url = `${root}${fileName}`;
      }
      list.push({
        id: `existing_${index}_${fileName}`,
        type: 'existing',
        url,
        fileName,
      });
    });

    newFiles.forEach((file, index) => {
      list.push({
        id: `new_${index}_${file.name}`,
        type: 'new',
        url: URL.createObjectURL(file),
        file,
      });
    });

    return list;
  }, [existingImages, newFiles]);

  const handleValidateAndAdd = (filesToAdd: File[]) => {
    setValidationError('');
    const validFiles: File[] = [];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    const totalCount = existingImages.length + newFiles.length + filesToAdd.length;
    if (totalCount > maxFiles) {
      setValidationError(`Maximum ${maxFiles} images allowed in total.`);
      return;
    }

    for (const file of filesToAdd) {
      if (!allowedTypes.includes(file.type)) {
        setValidationError(`Invalid file type: ${file.name}. Only JPG, PNG, and WEBP formats allowed.`);
        return;
      }
      if (file.size > maxSizeBytes) {
        setValidationError(`File too large: ${file.name}. Max size is ${maxSizeMB}MB.`);
        return;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      onChange({
        existingImages,
        newFiles: [...newFiles, ...validFiles],
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      handleValidateAndAdd(filesArray);
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const filesArray = Array.from(e.dataTransfer.files);
      handleValidateAndAdd(filesArray);
    }
  };

  const handleRemove = (index: number) => {
    const item = imageList[index];
    if (item.type === 'existing') {
      const updatedExisting = existingImages.filter((name) => name !== item.fileName);
      onChange({ existingImages: updatedExisting, newFiles });
    } else {
      const updatedNew = newFiles.filter((f) => f !== item.file);
      onChange({ existingImages, newFiles: updatedNew });
    }
  };

  const handleSetPrimary = (index: number) => {
    if (index === 0) return;
    const reordered = [...imageList];
    const [movedItem] = reordered.splice(index, 1);
    reordered.unshift(movedItem);

    const updatedExisting: string[] = [];
    const updatedNew: File[] = [];
    reordered.forEach((item) => {
      if (item.type === 'existing' && item.fileName) {
        updatedExisting.push(item.fileName);
      } else if (item.type === 'new' && item.file) {
        updatedNew.push(item.file);
      }
    });

    onChange({ existingImages: updatedExisting, newFiles: updatedNew });
  };

  const handleMove = (index: number, direction: 'left' | 'right') => {
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= imageList.length) return;

    const reordered = [...imageList];
    const temp = reordered[index];
    reordered[index] = reordered[targetIndex];
    reordered[targetIndex] = temp;

    const updatedExisting: string[] = [];
    const updatedNew: File[] = [];
    reordered.forEach((item) => {
      if (item.type === 'existing' && item.fileName) {
        updatedExisting.push(item.fileName);
      } else if (item.type === 'new' && item.file) {
        updatedNew.push(item.file);
      }
    });

    onChange({ existingImages: updatedExisting, newFiles: updatedNew });
  };

  return (
    <div className="space-y-4">
      {/* Hidden file input */}
      <input
        id="images"
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/jpg"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Modern Drag and Drop Zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 sm:p-4 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2 ${isDragging
            ? 'border-[#1AA7DE] bg-sky-50/80 scale-[1.01]'
            : error || validationError
              ? 'border-rose-300 bg-rose-50/30 hover:bg-rose-50/50'
              : 'border-slate-200 bg-slate-50/60 hover:bg-sky-50/40 hover:border-[#1AA7DE]'
          }`}
      >
        <div className="w-10 h-10 rounded-xl bg-white shadow-2xs border border-slate-100 flex items-center justify-center text-[#1AA7DE]">
          <UploadCloud className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs sm:text-sm font-bold text-slate-800 m-0">
            Click to upload or drag & drop product images
          </p>
          <p className="text-[11px] text-slate-400 font-medium mt-0.5 mb-0">
            JPG, PNG, or WEBP up to {maxSizeMB}MB each. Maximum {maxFiles} images.
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-white text-slate-700 border border-slate-200 shadow-2xs">
          <span>{imageList.length} / {maxFiles} Uploaded</span>
        </div>
      </div>

      {/* Validation Error Message */}
      {(validationError || error) && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{validationError || error}</span>
        </div>
      )}

      {/* Thumbnails Gallery Grid */}
      {imageList.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 pt-2">
          {imageList.map((item, index) => (
            <div
              key={item.id}
              className={`relative group rounded-xl overflow-hidden border bg-white shadow-2xs transition-all ${index === 0
                  ? 'border-[#1AA7DE] ring-2 ring-[#1AA7DE]/20'
                  : 'border-slate-200 hover:border-slate-300'
                }`}
              style={{ aspectRatio: '1/1' }}
            >
              <img
                src={item.url}
                alt={`Product thumbnail ${index + 1}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />

              {/* Top Badge & Delete Button */}
              <div className="absolute top-2 inset-x-2 flex items-center justify-between z-10">
                {index === 0 ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#1AA7DE] text-white shadow-sm">
                    <Star className="w-2.5 h-2.5 fill-current" /> Cover
                  </span>
                ) : (
                  <span />
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                  className="w-6 h-6 rounded-full bg-slate-900/80 hover:bg-rose-600 text-white flex items-center justify-center transition-colors shadow-sm ml-auto"
                  title="Remove image"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Bottom Reorder & Action Bar */}
              <div className="absolute bottom-2 inset-x-2 flex items-center justify-between z-10 bg-slate-900/75 backdrop-blur-xs rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {index !== 0 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetPrimary(index);
                    }}
                    className="text-[10px] font-bold text-white hover:text-sky-300 px-1 py-0.5 rounded transition-colors"
                  >
                    Set Cover
                  </button>
                ) : (
                  <span className="text-[10px] font-bold text-sky-300 px-1">Primary</span>
                )}

                <div className="flex items-center gap-0.5 ml-auto">
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMove(index, 'left');
                      }}
                      className="w-5 h-5 rounded hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                      title="Move left"
                    >
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                  )}
                  {index < imageList.length - 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMove(index, 'right');
                      }}
                      className="w-5 h-5 rounded hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                      title="Move right"
                    >
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
